import { createContext, useContext, useState, useEffect, useRef } from 'react';

const PWAContext = createContext(null);

const PWA_DISMISSED_KEY = 'pwa_install_dismissed'; // localStorage — persists forever

// Sync helper — reads standalone mode directly from the DOM (no React state delay)
const isStandaloneNow = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  window.navigator.standalone === true;

export const PWAProvider = ({ children }) => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(() => isStandaloneNow());
  const [isIOS, setIsIOS] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const bannerInitialized = useRef(false);
  const modalScheduled = useRef(false);

  useEffect(() => {
    const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(ios);

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);

    const onInstalled = () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
      setShowModal(false);
      setShowBanner(false);
      localStorage.setItem(PWA_DISMISSED_KEY, 'installed');
    };
    window.addEventListener('appinstalled', onInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setIsInstalled(true);
      setDeferredPrompt(null);
      setShowModal(false);
      setShowBanner(false);
    }
  };

  // Returns true if we should suppress the install/notification prompt
  const shouldSuppressPrompt = () => {
    if (isStandaloneNow()) return true;                                    // already installed as PWA
    if (localStorage.getItem(PWA_DISMISSED_KEY)) return true;             // user permanently dismissed
    if (typeof Notification !== 'undefined' && Notification.permission === 'granted') return true; // notifications already on
    return false;
  };

  // Called once when user authenticates
  const triggerPostAuthModal = () => {
    if (shouldSuppressPrompt()) return;

    // Already shown this session — show banner instead
    if (sessionStorage.getItem('pwa_modal_shown')) {
      if (!sessionStorage.getItem('pwa_banner_dismissed')) {
        setShowBanner(true);
      }
      return;
    }

    modalScheduled.current = true;
    setTimeout(() => {
      // Re-check right before showing (user might have installed during delay)
      if (!shouldSuppressPrompt()) {
        setShowModal(true);
      }
      modalScheduled.current = false;
    }, 900);
  };

  const initBanner = () => {
    if (bannerInitialized.current) return;
    bannerInitialized.current = true;
    if (shouldSuppressPrompt()) return;
    if (sessionStorage.getItem('pwa_banner_dismissed')) return;
    if (modalScheduled.current || !sessionStorage.getItem('pwa_modal_shown')) return;
    setShowBanner(true);
  };

  const dismissModal = () => {
    setShowModal(false);
    sessionStorage.setItem('pwa_modal_shown', '1');
    localStorage.setItem(PWA_DISMISSED_KEY, 'dismissed'); // never show again after "Not Now"
    setShowBanner(false);
  };

  const dismissBanner = () => {
    sessionStorage.setItem('pwa_banner_dismissed', '1');
    setShowBanner(false);
  };

  return (
    <PWAContext.Provider value={{
      deferredPrompt, isInstalled, isIOS,
      showModal, showBanner,
      handleInstall,
      triggerPostAuthModal, initBanner,
      dismissModal, dismissBanner,
    }}>
      {children}
    </PWAContext.Provider>
  );
};

export const usePWA = () => useContext(PWAContext);
